import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { validateVibeUploadFile, VIBE_IMAGE_MAX_MB, VIBE_VIDEO_MAX_MB } from '../utils/mediaUtils';
import '../styles/Status.css';
import '../styles/StatusUpload.css';
import EmojiPicker from './EmojiPicker';

function Status({ user, onBack, socket }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStatusViewer, setShowStatusViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('text'); // 'text', 'image', 'video'
  const [currentStatusIndex, setCurrentStatusIndex] = useState(0);
  const [duration, setDuration] = useState(24); // Duration in hours: 1, 24, 72, 168
  const fileInputRef = useRef(null);
  const [myStatuses, setMyStatuses] = useState([]);
  const [contactStatuses, setContactStatuses] = useState([]);
  const [isViewingContactStatus, setIsViewingContactStatus] = useState(false);
  const [currentContactUser, setCurrentContactUser] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [cameraFacingMode, setCameraFacingMode] = useState('user');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [currentStatusViewers, setCurrentStatusViewers] = useState([]);
  const [hasNewStatus, setHasNewStatus] = useState(false);
  const [newViewAnimation, setNewViewAnimation] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [statusLikes, setStatusLikes] = useState([]);
  const [showStatusLikes, setShowStatusLikes] = useState(false);
  const [commentLikes, setCommentLikes] = useState([]);
  const [showCommentLikes, setShowCommentLikes] = useState(false);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    fetchStatuses();
    fetchMyStatuses();
  }, []);

  // Refresh statuses when component becomes visible (e.g., when returning from settings)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchStatuses();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Socket event listeners for real-time status updates
  useEffect(() => {
    if (!socket) return;

    // Listen for new status uploads from contacts
    socket.on("newStatusAvailable", (data) => {
      //console.log('New status available from:', data.fromUserId);
      // Show new status indicator
      setHasNewStatus(true);
      // Refresh the status list to show new status
      fetchStatuses();
      // Auto-hide the indicator after 3 seconds
      setTimeout(() => setHasNewStatus(false), 3000);
    });

    // Listen for status deletions
    socket.on("statusDeleted", (data) => {
      //console.log('Status deleted:', data.statusId);
      // Remove deleted status from local state immediately
      setStatuses(prev => prev.filter(status => status._id !== data.statusId));
      setMyStatuses(prev => prev.filter(status => status._id !== data.statusId));
    });

    // Listen for real-time status view updates
    socket.on("statusViewUpdate", (data) => {
      //console.log('Status view update received:', data);
      const { statusId, viewer } = data;
      
      // Update the specific status with new view in myStatuses
      const ownerId = user.id?.toString?.();
      const viewerId = viewer.id?.toString?.();
      if (viewerId === ownerId) return;

      setMyStatuses(prev => prev.map(status => {
        if (status._id === statusId) {
          const updatedViews = [...(status.views || [])];
          // Check if this viewer already exists
          const existingViewIndex = updatedViews.findIndex(v => v.userId === viewer.id);
          if (existingViewIndex === -1) {
            // Add new viewer
            updatedViews.push({
              userId: viewer.id,
              viewedAt: viewer.viewedAt
            });
          }
          return { ...status, views: updatedViews };
        }
        return status;
      }));

      // Also update the viewers modal if it's currently open for this status
      if (showViewersModal && currentStatusViewers.length > 0) {
        const currentStatus = myStatuses.find(s => s._id === statusId);
        if (currentStatus) {
          const isAlreadyInList = currentStatusViewers.some(v => v.id === viewer.id);
          if (!isAlreadyInList) {
            setCurrentStatusViewers(prev => [viewer, ...prev]);
          }
        }
      }

      // Trigger animation for new view
      setNewViewAnimation(statusId);
      setTimeout(() => setNewViewAnimation(null), 1000);
    });

    // Listen for real-time status comment updates
    socket.on("statusCommentUpdate", (data) => {
      //console.log('Status comment update received:', data);
      const { statusId, comment } = data;
      
      // Update comments if viewing the commented status
      if (showComments && currentStatus && currentStatus._id === statusId) {
        setComments(prev => [...prev, comment]);
      }
    });

    // Listen for real-time comment like updates
    socket.on("commentLikeUpdate", (data) => {
      //console.log('Comment like update received:', data);
      const { statusId, commentId, action, likesCount } = data;
      
      // Update comment likes if viewing the status with this comment
      if (showComments && currentStatus && currentStatus._id === statusId) {
        setComments(prev => prev.map(comment => {
          if (comment._id === commentId) {
            return {
              ...comment,
              likesCount: likesCount,
              // Update the likes array if needed (for future features like showing who liked)
              likes: comment.likes || []
            };
          }
          return comment;
        }));
      }
    });

    // Listen for real-time status like updates
    socket.on("statusLikeUpdateGlobal", (data) => {
      //console.log('Status like update received:', data);
      const { statusId, action, likesCount, likerId } = data;
      
      // Update status likes in all status lists
      setStatuses(prev => prev.map(status => {
        if (status._id === statusId) {
          const updatedLikes = status.likes || [];
          if (action === 'liked') {
            // Add like if not already present
            if (!updatedLikes.some(like => like.userId === likerId)) {
              updatedLikes.push({ userId: likerId, likedAt: new Date() });
            }
          } else {
            // Remove like
            return {
              ...status,
              likes: updatedLikes.filter(like => like.userId !== likerId)
            };
          }
          return {
            ...status,
            likes: updatedLikes
          };
        }
        return status;
      }));

      // Update myStatuses if it's the user's own status
      setMyStatuses(prev => prev.map(status => {
        if (status._id === statusId) {
          const updatedLikes = status.likes || [];
          if (action === 'liked') {
            if (!updatedLikes.some(like => like.userId === likerId)) {
              updatedLikes.push({ userId: likerId, likedAt: new Date() });
            }
          } else {
            return {
              ...status,
              likes: updatedLikes.filter(like => like.userId !== likerId)
            };
          }
          return {
            ...status,
            likes: updatedLikes
          };
        }
        return status;
      }));
    });

    // Cleanup listeners on unmount
    return () => {
      socket.off("newStatusAvailable");
      socket.off("statusDeleted");
      socket.off("statusViewUpdate");
      socket.off("statusCommentUpdate");
      socket.off("commentLikeUpdate");
      socket.off("statusLikeUpdateGlobal");
    };
  }, [socket]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/status/all/${user.id}`);
      setStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch statuses:', error);
    }
  };

  const fetchMyStatuses = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/status/my/${user.id}`);
      setMyStatuses(response.data);
    } catch (error) {
      console.error('Failed to fetch my statuses:', error);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validation = validateVibeUploadFile(file);
      if (!validation.ok) {
        alert(validation.message);
        if (event.target) event.target.value = '';
        return;
      }
      setSelectedFile(file);
      if (file.type.startsWith('image/')) {
        setUploadType('image');
      } else if (file.type.startsWith('video/')) {
        setUploadType('video');
      }
      
      // Create preview URL and show preview modal
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    }
  };

  const handleUpload = async () => {
    if (!statusText.trim() && !selectedFile) return;

    if (selectedFile) {
      const validation = validateVibeUploadFile(selectedFile);
      if (!validation.ok) {
        alert(validation.message);
        return;
      }
    }

    setUploading(true);
    try {
      let statusData = {
        userId: user.id,
        text: statusText.trim(),
        type: uploadType,
        duration: duration // Include duration in hours
      };

      if (selectedFile) {
        const formData = new FormData();
        formData.append('statusFile', selectedFile);
        
        const uploadResponse = await axios.post(`${config.apiUrl}/api/status/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          maxContentLength: 30 * 1024 * 1024,
          maxBodyLength: 30 * 1024 * 1024,
        });
        
        statusData.fileUrl = uploadResponse.data.fileUrl;
        statusData.fileType = selectedFile.type;
      }

      const response = await axios.post(`${config.apiUrl}/api/status/create`, statusData);
      
      // Emit socket event to notify contacts about new status
      if (socket && response.data._id) {
        socket.emit("statusUploaded", {
          userId: user.id,
          statusId: response.data._id
        });
      }
      
      setStatusText('');
      setSelectedFile(null);
      setShowUploadModal(false);
      setUploadType('text');
      setDuration(24); // Reset to default
      
      // Refresh statuses
      fetchStatuses();
      fetchMyStatuses();
    } catch (error) {
      console.error('Failed to upload status:', error);
      alert('Failed to upload vibe. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteStatus = (statusId) => {
    setStatusToDelete(statusId);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteStatus = async () => {
    try {
      await axios.delete(`${config.apiUrl}/api/status/${statusToDelete}?userId=${user.id}`);
      
      // Emit socket event to notify contacts about status deletion
      if (socket) {
        socket.emit("statusDeleted", {
          userId: user.id,
          statusId: statusToDelete
        });
      }
      
      // Update local state immediately
      setMyStatuses(prev => prev.filter(status => status._id !== statusToDelete));
      setStatuses(prev => prev.filter(status => status._id !== statusToDelete));
      
      // Close confirmation and status viewer
      setShowDeleteConfirmation(false);
      setStatusToDelete(null);
      closeStatusViewer();
      
      // Refresh from server
      fetchMyStatuses();
      fetchStatuses();
    } catch (error) {
      console.error('Failed to delete status:', error);
      alert('Failed to delete vibe. Please try again.');
    }
  };

  const cancelDeleteStatus = () => {
    setShowDeleteConfirmation(false);
    setStatusToDelete(null);
  };

  const openCameraModal = () => {
    setShowCameraModal(true);
    setCameraFacingMode('user');
    startCamera('user');
  };

  const closeCameraModal = () => {
    setShowCameraModal(false);
    stopCamera();
  };

  const startCamera = async (mode = 'user') => {
    try {
      // Always release old camera first; mobile devices require this.
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      let stream;
      const base = {
        width: { ideal: 1280 },
        height: { ideal: 720 }
      };

      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            ...base,
            facingMode: { exact: mode }
          }
        });
      } catch (err) {
        // Try selecting by label (back/front) when facingMode exact is not supported.
        let targetCamera = null;
        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const cameras = devices.filter((d) => d.kind === 'videoinput');
          if (mode === 'environment') {
            targetCamera = cameras.find((c) => /back|rear|environment/i.test(c.label));
          } else {
            targetCamera = cameras.find((c) => /front|user|facetime/i.test(c.label));
          }
        } catch (_) {}

        if (targetCamera) {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              ...base,
              deviceId: { exact: targetCamera.deviceId }
            }
          });
        } else {
          // Final fallback: facingMode hint (non-exact), then any available camera.
          try {
            stream = await navigator.mediaDevices.getUserMedia({
              video: {
                ...base,
                facingMode: mode
              }
            });
          } catch (_) {
            stream = await navigator.mediaDevices.getUserMedia({ video: true });
          }
        }
      }

      cameraStreamRef.current = stream;
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Could not access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    const activeStream = cameraStreamRef.current || cameraStream;
    if (activeStream) {
      activeStream.getTracks().forEach((track) => track.stop());
    }
    cameraStreamRef.current = null;
    setCameraStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob
      canvas.toBlob(async (blob) => {
        if (blob) {
          // Create a file from the blob
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setSelectedFile(file);
          setUploadType('image');
          
          // Create preview URL
          const reader = new FileReader();
          reader.onloadend = () => {
            // Close camera modal and open upload modal
            closeCameraModal();
            setShowUploadModal(true);
          };
          reader.readAsDataURL(file);
        }
      }, 'image/jpeg', 0.8);
    }
  };

  const switchCamera = async () => {
    if (!cameraStreamRef.current && !cameraStream) return;
    const nextMode = cameraFacingMode === 'user' ? 'environment' : 'user';
    setCameraFacingMode(nextMode);
    await startCamera(nextMode);
  };

  const getViewUserId = (view) =>
    view?.userId?._id?.toString?.() || view?.userId?.toString?.() || null;

  const getViewsExcludingOwner = (views, ownerId) =>
    (views || []).filter((view) => {
      const viewerId = getViewUserId(view);
      return viewerId && viewerId !== ownerId;
    });

  const showStatusViewers = async (status) => {
    try {
      const viewsForList = getViewsExcludingOwner(status.views, user.id?.toString?.());

      // Get detailed viewer information
      const viewersWithDetails = await Promise.all(
        viewsForList.map(async (view) => {
          try {
            const response = await axios.get(`${config.apiUrl}/api/auth/profile/${view.userId}`);
            return {
              ...response.data,
              viewedAt: view.viewedAt
            };
          } catch (error) {
            console.error('Error fetching viewer details:', error);
            return {
              id: view.userId,
              username: 'Unknown User',
              profileImage: null,
              viewedAt: view.viewedAt
            };
          }
        })
      );

      // Sort by most recent view first
      viewersWithDetails.sort((a, b) => new Date(b.viewedAt) - new Date(a.viewedAt));
      
      setCurrentStatusViewers(viewersWithDetails);
      setShowViewersModal(true);
    } catch (error) {
      console.error('Error fetching status viewers:', error);
    }
  };

  const closeViewersModal = () => {
    setShowViewersModal(false);
    setCurrentStatusViewers([]);
  };

  const formatViewTime = (timestamp) => {
    const now = new Date();
    const viewTime = new Date(timestamp);
    const diffMs = now - viewTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return viewTime.toLocaleDateString();
  };

  const fetchComments = async (statusId) => {
    try {
      setLoadingComments(true);
      const response = await axios.get(`${config.apiUrl}/api/status/${statusId}/comments`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const addComment = async () => {
    if (!newComment.trim() || !currentStatus) return;

    try {
      const response = await axios.post(`${config.apiUrl}/api/status/${currentStatus._id}/comment`, {
        userId: user.id,
        text: newComment.trim()
      });

      // Add comment to local state immediately
      setComments(prev => [...prev, response.data.comment]);
      setNewComment('');
      setShowEmojiPicker(false); // Close emoji picker after sending

      // Emit socket event to notify status owner
      if (socket && currentStatus.userId && currentStatus.userId._id !== user.id) {
        socket.emit("statusCommented", {
          statusId: currentStatus._id,
          commenterId: user.id,
          statusOwnerId: currentStatus.userId._id,
          comment: response.data.comment
        });
      }

    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment. Please try again.');
    }
  };

  const handleEmojiSelect = (emoji) => {
    setNewComment(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(prev => !prev);
  };

  const openComments = (status) => {
    setShowComments(true);
    fetchComments(status._id);
  };

  const closeComments = () => {
    setShowComments(false);
    setComments([]);
    setNewComment('');
  };

  const toggleCommentLike = async (commentId) => {
    if (!currentStatus) return;

    try {
      const response = await axios.post(`${config.apiUrl}/api/status/${currentStatus._id}/comment/${commentId}/like`, {
        userId: user.id
      });

      // Update local state immediately
      setComments(prev => prev.map(comment => {
        if (comment._id === commentId) {
          const isLiked = comment.likes?.some(like => like.userId === user.id);
          const newLikes = isLiked 
            ? comment.likes.filter(like => like.userId !== user.id)
            : [...(comment.likes || []), { userId: user.id, likedAt: new Date() }];
          
          return {
            ...comment,
            likes: newLikes,
            likesCount: newLikes.length
          };
        }
        return comment;
      }));

      // Emit socket event for real-time updates
      if (socket) {
        socket.emit("commentLiked", {
          statusId: currentStatus._id,
          commentId: commentId,
          likerId: user.id,
          statusOwnerId: currentStatus.userId._id || currentStatus.userId,
          action: response.data.action,
          likesCount: response.data.likesCount
        });
      }

    } catch (error) {
      console.error('Failed to toggle comment like:', error);
      // Revert optimistic update on error
      fetchComments(currentStatus._id);
    }
  };

  const toggleStatusLike = async () => {
    if (!currentStatus) return;

    try {
      const response = await axios.post(`${config.apiUrl}/api/status/${currentStatus._id}/like`, {
        userId: user.id
      });

      // Emit socket event for real-time updates
      if (socket) {
        socket.emit("statusLiked", {
          statusId: currentStatus._id,
          likerId: user.id,
          statusOwnerId: currentStatus.userId._id || currentStatus.userId,
          action: response.data.action,
          likesCount: response.data.likesCount
        });
      }

    } catch (error) {
      console.error('Failed to toggle status like:', error);
    }
  };

  const fetchStatusLikes = async (statusId) => {
    try {
      const response = await axios.get(`${config.apiUrl}/api/status/${statusId}/likes`);
      setStatusLikes(response.data.likes);
      setShowStatusLikes(true);
    } catch (error) {
      console.error('Failed to fetch status likes:', error);
    }
  };

  const closeStatusLikes = () => {
    setShowStatusLikes(false);
    setStatusLikes([]);
  };

  const fetchCommentLikes = async (commentId) => {
    if (!currentStatus) return;
    
    try {
      const response = await axios.get(`${config.apiUrl}/api/status/${currentStatus._id}/comment/${commentId}/likes`);
      setCommentLikes(response.data.likes);
      setSelectedCommentId(commentId);
      setShowCommentLikes(true);
    } catch (error) {
      console.error('Failed to fetch comment likes:', error);
    }
  };

  const closeCommentLikes = () => {
    setShowCommentLikes(false);
    setCommentLikes([]);
    setSelectedCommentId(null);
  };

  const openUploadModal = (type) => {
    setUploadType(type);
    setShowUploadModal(true);
    setStatusText('');
    setSelectedFile(null);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setStatusText('');
    setSelectedFile(null);
    setUploadType('text');
    setDuration(24); // Reset to default
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openUpload = searchParams.get('openUpload');

    if (openUpload === 'text' && !showUploadModal) {
      openUploadModal('text');
      searchParams.delete('openUpload');
      navigate(
        {
          pathname: location.pathname,
          search: searchParams.toString() ? `?${searchParams.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate, showUploadModal]);

  const closePreviewModal = () => {
    setShowPreviewModal(false);
    // Clean up preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const confirmUploadFromPreview = () => {
    setShowPreviewModal(false);
    // Keep the selected file and preview URL for upload
  };

  const cancelUploadFromPreview = () => {
    setSelectedFile(null);
    setUploadType('text');
    closePreviewModal();
  };

  const openStatusViewer = () => {
    if (myStatuses.length > 0) {
      setShowStatusViewer(true);
      setCurrentStatusIndex(0);
      setIsViewingContactStatus(false);
    }
  };

  const openContactStatusViewer = (userId, userStatuses) => {
    const contactUser = userStatuses[0].userId;
    setCurrentContactUser(contactUser);
    setContactStatuses(userStatuses);
    setShowStatusViewer(true);
    setCurrentStatusIndex(0);
    setIsViewingContactStatus(true);
    
    // Mark statuses as viewed
    markStatusesAsViewed(userStatuses);
  };

  const markStatusesAsViewed = async (userStatuses) => {
    try {
      // Immediately update local state for instant UI feedback
      setStatuses(prevStatuses => {
        return prevStatuses.map(status => {
          const statusToUpdate = userStatuses.find(us => us._id === status._id);
          if (statusToUpdate) {
            // Add current user to views if not already present
            const updatedViews = status.views || [];
            const alreadyViewed = updatedViews.some(view => view.userId === user.id);
            if (!alreadyViewed) {
              updatedViews.push({ userId: user.id, viewedAt: new Date() });
            }
            return { ...status, views: updatedViews };
          }
          return status;
        });
      });

      // Then make API calls to persist the changes and emit socket events
      for (const status of userStatuses) {
        // Check if already viewed to avoid duplicate API calls
        const alreadyViewed = status.views?.some(view => view.userId === user.id);
        
        if (!alreadyViewed) {
          await axios.post(`${config.apiUrl}/api/status/${status._id}/view`, {
            userId: user.id
          });

          // Emit socket event to notify status owner about the view
          if (socket && status.userId && status.userId._id !== user.id) {
            socket.emit("statusViewed", {
              statusId: status._id,
              viewerId: user.id,
              statusOwnerId: status.userId._id
            });
          }
        }
      }
      
    } catch (error) {
      console.error('Failed to mark statuses as viewed:', error);
      // If API call fails, refresh from server to get correct state
      fetchStatuses();
    }
  };

  const closeStatusViewer = () => {
    setShowStatusViewer(false);
    setCurrentStatusIndex(0);
    setIsViewingContactStatus(false);
    setCurrentContactUser(null);
    setContactStatuses([]);
    
    // Refresh the status list when closing viewer to reflect any view changes
    fetchStatuses();
  };

  const getCurrentStatuses = () => {
    return isViewingContactStatus ? contactStatuses : myStatuses;
  };

  const nextStatus = () => {
    const currentStatuses = getCurrentStatuses();
    if (currentStatusIndex < currentStatuses.length - 1) {
      setCurrentStatusIndex(currentStatusIndex + 1);
    }
  };

  const previousStatus = () => {
    if (currentStatusIndex > 0) {
      setCurrentStatusIndex(currentStatusIndex - 1);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const statusTime = new Date(timestamp);
    const diffMs = now - statusTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return statusTime.toLocaleDateString();
  };

  const formatDuration = (durationHours) => {
    if (durationHours === 1) return '1 Hour';
    if (durationHours === 24) return '1 Day';
    if (durationHours === 72) return '3 Days';
    if (durationHours === 168) return '1 Week';
    return `${durationHours} Hours`;
  };

  const getTimeRemaining = (expiresAt) => {
    if (!expiresAt) return 'Unknown';
    
    try {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diffMs = expiry - now;
      
      if (diffMs <= 0) return 'Expired';
      
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 60) return `${diffMins}m remaining`;
      if (diffHours < 24) return `${diffHours}h remaining`;
      if (diffDays < 7) return `${diffDays}d remaining`;
      return `${Math.floor(diffDays)}d remaining`;
    } catch (error) {
      console.error('Error calculating time remaining:', error);
      return 'Unknown';
    }
  };

  const currentStatuses = getCurrentStatuses();
  const currentStatus = currentStatuses[currentStatusIndex];
  const currentUser = isViewingContactStatus ? currentContactUser : user;
  const myVibeOtherViewCount =
    currentStatus && !isViewingContactStatus
      ? getViewsExcludingOwner(currentStatus.views, user.id?.toString?.()).length
      : 0;

  // Helper function to get expiresAt, calculating if missing
  const getExpiresAt = (status) => {
    if (status.expiresAt) {
      return new Date(status.expiresAt);
    }
    if (status.createdAt && status.duration) {
      return new Date(new Date(status.createdAt).getTime() + status.duration * 60 * 60 * 1000);
    }
    if (status.createdAt) {
      // Default to 24 hours if duration not specified
      return new Date(new Date(status.createdAt).getTime() + 24 * 60 * 60 * 1000);
    }
    return null;
  };

  return (
    <div className="status-container">
      {/* Header */}
      <div className="status-header">
        <div className="status-header-content">
          <h2>Vibe</h2>
          <div className="status-header-buttons">
            <button 
              className="refresh-status-btn"
              onClick={() => {
                fetchStatuses();
                fetchMyStatuses();
              }}
              title="Refresh vibe updates"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15"/>
              </svg>
            </button>
            <button 
              className="add-status-btn"
              onClick={() => openUploadModal('text')}
              title="Add new vibe"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14"/>
              </svg>
            </button>
          </div>
        </div>
        
        {/* New Status Notification */}
        {hasNewStatus && (
          <div className="new-status-notification">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
            New vibe updates available
          </div>
        )}
      </div>

      {/* My Status Section */}
      <div className="my-status-section">
        <h3>My Vibe</h3>
        <div className="my-status-item" onClick={() => openUploadModal('text')}>
          <div className="status-avatar add-status">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14"/>
            </svg>
          </div>
          <div className="status-info">
            <span className="status-name">Add to my vibe</span>
            <span className="status-time">Tap to add vibe update</span>
          </div>
        </div>
        
        {myStatuses.length > 0 && (
          <div className="my-status-item clickable" onClick={openStatusViewer}>
            <div className="status-avatar">
              {myStatuses[0].fileUrl && myStatuses[0].fileType?.startsWith('image/') ? (
                <img src={`${config.apiUrl}${myStatuses[0].fileUrl}`} alt="Vibe" />
              ) : (
                <div className="status-avatar-placeholder notranslate">
                  {user.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="status-info">
              <span className="status-name">My vibe</span>
              <span className="status-time">{myStatuses.length} vibe{myStatuses.length !== 1 ? 's' : ''} • Tap to view</span>
            </div>
          </div>
        )}
      </div>

      {/* Recent Updates Section */}
      <div className="recent-updates-section">
        <h3>Recent updates</h3>
        {statuses.length > 0 ? (
          (() => {
            // Group statuses by user
            const statusesByUser = statuses.reduce((acc, status) => {
              const userId = status.userId._id || status.userId;
              if (!acc[userId]) {
                acc[userId] = {
                  user: status.userId,
                  statuses: []
                };
              }
              acc[userId].statuses.push(status);
              return acc;
            }, {});

            // Convert to array and sort by view status first, then by most recent
            const userStatusGroups = Object.values(statusesByUser)
              .filter(group => group.user._id !== user.id) // Exclude current user from recent updates
              .sort((a, b) => {
                // Check if groups have unviewed statuses
                const aHasUnviewed = a.statuses.some(status => 
                  !status.views?.some(view => view.userId === user.id)
                );
                const bHasUnviewed = b.statuses.some(status => 
                  !status.views?.some(view => view.userId === user.id)
                );
                
                // If one has unviewed and other doesn't, prioritize unviewed
                if (aHasUnviewed && !bHasUnviewed) return -1;
                if (!aHasUnviewed && bHasUnviewed) return 1;
                
                // If both have same view status, sort by most recent
                return new Date(b.statuses[0].createdAt) - new Date(a.statuses[0].createdAt);
              });

            return userStatusGroups.map((group) => {
              // Check if there are unviewed statuses
              const hasUnviewedStatuses = group.statuses.some(status => 
                !status.views?.some(view => view.userId === user.id)
              );
              
              return (
                <div key={group.user._id} className="status-item" onClick={() => openContactStatusViewer(group.user._id, group.statuses)}>
                  <div className={`status-avatar ${hasUnviewedStatuses ? 'unviewed' : 'viewed'}`}>
                    {group.statuses[0].fileUrl && group.statuses[0].fileType?.startsWith('image/') ? (
                      <img src={`${config.apiUrl}${group.statuses[0].fileUrl}`} alt="Vibe" />
                    ) : group.user?.profileImage ? (
                      <img src={`${config.apiUrl}${group.user.profileImage}`} alt={group.user.username} />
                    ) : (
                      <div className="status-avatar-placeholder notranslate">
                        {group.user?.username?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <div className="status-info">
                    <span className="status-name notranslate">{group.user?.username || 'Unknown User'}</span>
                    <span className="status-time">
                      {group.statuses.length} vibe{group.statuses.length !== 1 ? 's' : ''} • {formatTimeAgo(group.statuses[0].createdAt)}
                    </span>
                  </div>
                </div>
              );
            });
          })()
        ) : (
          <p className="no-statuses">No recent vibe updates from contacts</p>
        )}
      </div>

      {/* Status Viewer Modal */}
      {showStatusViewer && currentStatus && (
        <div className="status-viewer-modal">
          <div className="status-viewer-content">
            {/* Header */}
            <div className="status-viewer-header">
              <div className="status-viewer-user-info">
                <div className="status-viewer-avatar">
                  {currentUser?.profileImage ? (
                    <img src={`${config.apiUrl}${currentUser.profileImage}`} alt={currentUser.username} />
                  ) : (
                    <div className="status-viewer-avatar-placeholder notranslate">
                      {currentUser?.username?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
                <div className="status-viewer-details">
                  <span className="status-viewer-name notranslate">
                    {isViewingContactStatus ? (currentUser?.username || 'Unknown User') : 'My Vibe'}
                  </span>
                  {isViewingContactStatus && currentStatus && (
                    <span className="vibe-duration-display">
                      {currentStatus.duration ? formatDuration(currentStatus.duration) : '1 Day'} vibe
                      {' • '}
                      {(() => {
                        const expiresAt = getExpiresAt(currentStatus);
                        return expiresAt ? getTimeRemaining(expiresAt) : 'Unknown';
                      })()}
                    </span>
                  )}
                  <span className="status-viewer-time">
                    {formatTimeAgo(currentStatus.createdAt)}
                    {!isViewingContactStatus && currentStatus && (
                      <span className="duration-info">
                        {' • '}
                        {currentStatus.duration ? `Set for ${formatDuration(currentStatus.duration)}` : 'Set for 1 Day'}
                        {' • '}
                        {(() => {
                          const expiresAt = getExpiresAt(currentStatus);
                          return expiresAt ? getTimeRemaining(expiresAt) : 'Unknown';
                        })()}
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="status-viewer-actions">
                {!isViewingContactStatus && myVibeOtherViewCount > 0 && (
                  <button 
                    className={`viewers-btn ${newViewAnimation === currentStatus._id ? 'new-view-pulse' : ''}`}
                    onClick={() => showStatusViewers(currentStatus)}
                    title={`${myVibeOtherViewCount} view${myVibeOtherViewCount !== 1 ? 's' : ''}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span className="viewers-count">{myVibeOtherViewCount}</span>
                  </button>
                )}
                {!isViewingContactStatus && currentStatus.likes && currentStatus.likes.length > 0 && (
                  <button 
                    className="likes-btn"
                    onClick={() => fetchStatusLikes(currentStatus._id)}
                    title={`${currentStatus.likes.length} like${currentStatus.likes.length !== 1 ? 's' : ''}`}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#ff3040" stroke="#ff3040" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    <span className="likes-count">{currentStatus.likes.length}</span>
                  </button>
                )}
                {!isViewingContactStatus && (
                  <button 
                    className="delete-status-btn"
                    onClick={() => handleDeleteStatus(currentStatus._id)}
                    title="Delete vibe"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                    </svg>
                  </button>
                )}
                {isViewingContactStatus && (
                  <button 
                    className={`status-like-btn ${currentStatus.likes?.some(like => like.userId === user.id) ? 'liked' : ''}`}
                    onClick={toggleStatusLike}
                    title={currentStatus.likes?.some(like => like.userId === user.id) ? 'Unlike' : 'Like'}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                    {currentStatus.likes && currentStatus.likes.length > 0 && (
                      <span className="status-like-count">
                        {currentStatus.likes.length}
                      </span>
                    )}
                  </button>
                )}
                <button 
                  className="comment-btn"
                  onClick={() => openComments(currentStatus)}
                  title="Comments"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                  </svg>
                  {currentStatus.comments && currentStatus.comments.length > 0 && (
                    <span className="comment-count">{currentStatus.comments.length}</span>
                  )}
                </button>
                <button className="close-btn" onClick={closeStatusViewer}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Status Content */}
            <div className="status-viewer-body">
              {currentStatus.text && (
                <div className="status-text-content notranslate">
                  <p>{currentStatus.text}</p>
                </div>
              )}
              
              {currentStatus.fileUrl && (
                <div className="status-media-content">
                  {currentStatus.fileType?.startsWith('image/') ? (
                    <img 
                      src={`${config.apiUrl}${currentStatus.fileUrl}`} 
                      alt="Vibe" 
                      className="status-image"
                    />
                  ) : currentStatus.fileType?.startsWith('video/') ? (
                    <video 
                      src={`${config.apiUrl}${currentStatus.fileUrl}`} 
                      controls 
                      className="status-video"
                    />
                  ) : null}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div className="status-viewer-navigation">
              <button 
                className="nav-btn prev-btn"
                onClick={previousStatus}
                disabled={currentStatusIndex === 0}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6"/>
                </svg>
              </button>
              
              <div className="status-progress">
                {currentStatuses.map((_, index) => (
                  <div 
                    key={index} 
                    className={`progress-dot ${index === currentStatusIndex ? 'active' : ''}`}
                    onClick={() => setCurrentStatusIndex(index)}
                  />
                ))}
              </div>
              
              <button 
                className="nav-btn next-btn"
                onClick={nextStatus}
                disabled={currentStatusIndex === currentStatuses.length - 1}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="status-upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add to my vibe</h3>
              <button className="close-btn" onClick={closeUploadModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body">
              {/* Upload Type Buttons */}
              <div className="upload-type-buttons">
                <button 
                  className={`upload-type-btn ${uploadType === 'text' ? 'active' : ''}`}
                  onClick={() => setUploadType('text')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                    <path d="M14 2v6h6"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10,9 9,9 8,9"/>
                  </svg>
                  Text
                </button>
                <button 
                  className="upload-type-btn"
                  onClick={openCameraModal}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
                    <circle cx="12" cy="13" r="4"/>
                  </svg>
                  Camera
                </button>
                <button 
                  className={`upload-type-btn ${uploadType === 'image' ? 'active' : ''}`}
                  onClick={() => setUploadType('image')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  Image
                </button>
                <button 
                  className={`upload-type-btn ${uploadType === 'video' ? 'active' : ''}`}
                  onClick={() => setUploadType('video')}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="23 7 16 12 23 17 23 7"/>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                  </svg>
                  Video
                </button>
              </div>

              {/* Text Input */}
              <div className="status-text-input">
                <textarea
                  placeholder="Type a vibe message..."
                  value={statusText}
                  onChange={(e) => setStatusText(e.target.value)}
                  rows="4"
                />
              </div>

              {/* File Upload */}
              {(uploadType === 'image' || uploadType === 'video') && (
                <div className="file-upload-section">
                  <p className="vibe-upload-size-hint">
                    {uploadType === 'video'
                      ? `Videos up to ${VIBE_VIDEO_MAX_MB}MB`
                      : `Images up to ${VIBE_IMAGE_MAX_MB}MB`}
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={uploadType === 'image' ? 'image/*' : 'video/*'}
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <button 
                    className="file-upload-btn"
                    onClick={() => fileInputRef.current.click()}
                  >
                    {selectedFile ? selectedFile.name : `Choose ${uploadType}`}
                  </button>
                  {selectedFile && (
                    <div className="selected-file">
                      <span>{selectedFile.name}</span>
                      <button 
                        className="remove-file-btn"
                        onClick={() => setSelectedFile(null)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Duration Selection */}
              <div className="duration-selection-section">
                <label className="duration-label">Vibe duration:</label>
                <div className="duration-options">
                  <button
                    type="button"
                    className={`duration-option ${duration === 1 ? 'active' : ''}`}
                    onClick={() => setDuration(1)}
                  >
                    1 Hour
                  </button>
                  <button
                    type="button"
                    className={`duration-option ${duration === 24 ? 'active' : ''}`}
                    onClick={() => setDuration(24)}
                  >
                    1 Day
                  </button>
                  <button
                    type="button"
                    className={`duration-option ${duration === 72 ? 'active' : ''}`}
                    onClick={() => setDuration(72)}
                  >
                    3 Days
                  </button>
                  <button
                    type="button"
                    className={`duration-option ${duration === 168 ? 'active' : ''}`}
                    onClick={() => setDuration(168)}
                  >
                    1 Week
                  </button>
                </div>
              </div>

              {/* Upload Button */}
              <div className="modal-actions">
                <button 
                  className="upload-btn"
                  onClick={handleUpload}
                  disabled={uploading || (!statusText.trim() && !selectedFile)}
                >
                  {uploading ? 'Uploading...' : 'Add to my vibe'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="status-upload-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Delete Vibe</h3>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete this vibe?</p>
              
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={cancelDeleteStatus}
                >
                  Cancel
                </button>
                <button 
                  className="delete-btn"
                  onClick={confirmDeleteStatus}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCameraModal && (
        <div className="camera-modal">
          <div className="camera-content">
            <div className="camera-header">
              <h3>Take Photo</h3>
              <button className="close-btn" onClick={closeCameraModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="camera-body">
              <div className="camera-preview">
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline 
                  muted
                  className="camera-video"
                />
                <canvas 
                  ref={canvasRef} 
                  style={{ display: 'none' }}
                />
              </div>
              
              <div className="camera-controls">
                <button 
                  className="camera-switch-btn"
                  onClick={switchCamera}
                  title="Switch Camera"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 8l4-4m0 0l-4-4m4 4H7a4 4 0 000 8h.01"/>
                    <path d="M7 16l-4 4m0 0l4 4m-4-4h10a4 4 0 000-8h-.01"/>
                  </svg>
                </button>
                
                <button 
                  className="camera-capture-btn"
                  onClick={capturePhoto}
                  title="Take Photo"
                >
                  <div className="capture-ring">
                    <div className="capture-circle"></div>
                  </div>
                </button>
                
                <div className="camera-placeholder"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && selectedFile && (
        <div className="status-upload-modal">
          <div className="modal-content preview-modal">
            <div className="modal-header">
              <h3>Preview {uploadType === 'image' ? 'Image' : 'Video'}</h3>
              <button className="close-btn" onClick={closePreviewModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body preview-body">
              <div className={`preview-container ${selectedFile && selectedFile.size > 5 * 1024 * 1024 ? 'large-image' : ''}`}>
                {uploadType === 'image' ? (
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="preview-image"
                    onLoad={(e) => {
                      // Add large-image class if image is very large or tall
                      const img = e.target;
                      const container = e.target.parentElement;
                      if (img.naturalWidth > 2000 || img.naturalHeight > 2000) {
                        container.classList.add('large-image');
                      }
                      // Also add for tall images (height > width * 1.5)
                      if (img.naturalHeight > img.naturalWidth * 1.5) {
                        container.classList.add('large-image');
                      }
                    }}
                  />
                ) : (
                  <video 
                    src={previewUrl} 
                    controls 
                    className="preview-video"
                    onLoadedMetadata={(e) => {
                      // Add large-image class if video is very large or tall
                      const video = e.target;
                      const container = e.target.parentElement;
                      if (video.videoWidth > 2000 || video.videoHeight > 2000) {
                        container.classList.add('large-image');
                      }
                      // Also add for tall videos (height > width * 1.5)
                      if (video.videoHeight > video.videoWidth * 1.5) {
                        container.classList.add('large-image');
                      }
                    }}
                  />
                )}
              </div>
              
              <div className="preview-info">
                <p><strong>File:</strong> {selectedFile.name}</p>
                <p><strong>Size:</strong> {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
                <p><strong>Type:</strong> {selectedFile.type}</p>
              </div>
              
              <div className="modal-actions">
                <button 
                  className="cancel-btn"
                  onClick={cancelUploadFromPreview}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-btn"
                  onClick={confirmUploadFromPreview}
                >
                  Use This {uploadType === 'image' ? 'Image' : 'Video'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Viewers Modal */}
      {showViewersModal && (
        <div className="status-upload-modal">
          <div className="modal-content viewers-modal">
            <div className="modal-header">
              <h3>Viewed by {currentStatusViewers.length}</h3>
              <button className="close-btn" onClick={closeViewersModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body viewers-list">
              {currentStatusViewers.length > 0 ? (
                currentStatusViewers.map((viewer) => (
                  <div key={viewer.id} className="viewer-item">
                    <div className="viewer-avatar">
                      {viewer.profileImage ? (
                        <img src={`${config.apiUrl}${viewer.profileImage}`} alt={viewer.username} />
                      ) : (
                        <div className="viewer-avatar-placeholder notranslate">
                          {viewer.username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="viewer-info">
                      <span className="viewer-name notranslate">{viewer.username || 'Unknown User'}</span>
                      <span className="viewer-time">{formatViewTime(viewer.viewedAt)}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-viewers">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  <p>No one has viewed this vibe yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && (
        <div className="status-upload-modal">
          <div className="modal-content comments-modal">
            <div className="modal-header">
              <h3>Comments</h3>
              <button className="close-btn" onClick={closeComments}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body comments-body">
              <div className="comments-list">
                {loadingComments ? (
                  <div className="loading-comments">
                    <p>Loading comments...</p>
                  </div>
                ) : comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment._id} className="comment-item">
                      <div className="comment-avatar">
                        {comment.user.profileImage ? (
                          <img src={`${config.apiUrl}${comment.user.profileImage}`} alt={comment.user.username} />
                        ) : (
                          <div className="comment-avatar-placeholder notranslate">
                            {comment.user.username?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                      </div>
                      <div className="comment-content">
                        <div className="comment-header">
                          <span className="comment-username notranslate">{comment.user.username}</span>
                          <span className="comment-time">{formatViewTime(comment.createdAt)}</span>
                        </div>
                        <div className="comment-text notranslate">{comment.text}</div>
                        <div className="comment-actions">
                          <button 
                            className={`comment-like-btn ${comment.likes?.some(like => like.userId === user.id) ? 'liked' : ''}`}
                            onClick={() => toggleCommentLike(comment._id)}
                            title={comment.likes?.some(like => like.userId === user.id) ? 'Unlike' : 'Like'}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                            </svg>
                            {comment.likesCount > 0 && (
                              <span 
                                className="like-count clickable" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchCommentLikes(comment._id);
                                }}
                                title="See who liked this comment"
                              >
                                {comment.likesCount}
                              </span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-comments">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                    <p>No comments yet</p>
                    <span>Be the first to comment</span>
                  </div>
                )}
              </div>
              
              <div className="comment-input-section">
                <div className="comment-input-container">
                  <input
                    type="text"
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addComment()}
                    maxLength={500}
                  />
                  <button 
                    className="emoji-picker-btn"
                    onClick={toggleEmojiPicker}
                    title="Add emoji"
                  >
                    😊
                  </button>
                  <button 
                    className="send-comment-btn"
                    onClick={addComment}
                    disabled={!newComment.trim()}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22,2 15,22 11,13 2,9 22,2"/>
                    </svg>
                  </button>
                </div>
                
                {showEmojiPicker && (
                  <EmojiPicker
                    onEmojiSelect={handleEmojiSelect}
                    onClose={() => setShowEmojiPicker(false)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Likes Modal */}
      {showStatusLikes && (
        <div className="status-upload-modal">
          <div className="modal-content viewers-modal">
            <div className="modal-header">
              <h3>❤️ Liked by {statusLikes.length} {statusLikes.length === 1 ? 'person' : 'people'}</h3>
              <button className="close-btn" onClick={closeStatusLikes}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body viewers-list">
              {statusLikes.length > 0 ? (
                statusLikes.map((like) => (
                  <div key={like._id} className="viewer-item">
                    <div className="viewer-avatar">
                      {like.user.profileImage ? (
                        <img src={`${config.apiUrl}${like.user.profileImage}`} alt={like.user.username} />
                      ) : (
                        <div className="viewer-avatar-placeholder notranslate">
                          {like.user.username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="viewer-info">
                      <span className="viewer-name notranslate">{like.user.username || 'Unknown User'}</span>
                      <span className="viewer-time">{formatViewTime(like.likedAt)}</span>
                    </div>
                    <div className="like-indicator">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#ff3040" stroke="#ff3040" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-viewers">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <p>No likes yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comment Likes Modal */}
      {showCommentLikes && (
        <div className="status-upload-modal">
          <div className="modal-content viewers-modal">
            <div className="modal-header">
              <h3>❤️ Liked by {commentLikes.length} {commentLikes.length === 1 ? 'person' : 'people'}</h3>
              <button className="close-btn" onClick={closeCommentLikes}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            
            <div className="modal-body viewers-list">
              {commentLikes.length > 0 ? (
                commentLikes.map((like) => (
                  <div key={like._id} className="viewer-item">
                    <div className="viewer-avatar">
                      {like.user.profileImage ? (
                        <img src={`${config.apiUrl}${like.user.profileImage}`} alt={like.user.username} />
                      ) : (
                        <div className="viewer-avatar-placeholder notranslate">
                          {like.user.username?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                      )}
                    </div>
                    <div className="viewer-info">
                      <span className="viewer-name notranslate">{like.user.username || 'Unknown User'}</span>
                      <span className="viewer-time">{formatViewTime(like.likedAt)}</span>
                    </div>
                    <div className="like-indicator">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#ff3040" stroke="#ff3040" strokeWidth="2">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-viewers">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  <p>No likes yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Status;
        